import React from 'react';
import Styled from 'styled-components';
import { Trans } from '@lingui/react';
import { Flex, Container as container } from '@components/layout/Grid';
import { ProgressBar, ProgressBarValue } from './ProgramsHeaderSection';
import ProgramExpandableInstructors from './ProgramExpandableInstructors';
import ContinueProgramCard from './ContinueProgramCard';
import {
  CourseAccess,
  GenericProgram,
  ProgramAccess,
  ProgramCategory,
} from '@gql/generated';
import { DownloadPDF as downloadButton } from './GuestProgramHeaderSection';
import { CurrentElement, ProgramInstructorsFragment } from '@gql/generated';

const Container = Styled(container)``;

const ProgramName = Styled.h1`
  margin: 0;
`;

const ProgramDetails = Styled.div`
  flex: 1;
  max-width: 600px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-right: 20px;
  margin-bottom: 20px;

  @media(min-width: ${({ theme }) => theme.viewports.tablet}px){
    min-width: 350px;
  }
`;

const DownloadButton = Styled(downloadButton)`
  margin-right: 20px;
`;

const ProgressText = Styled.p`
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  margin-left: 10px;
`;

const StyledProgressBar = Styled(ProgressBar)`
  flex: 1;
  background-color: rgba(102, 124, 145, .2);
`;

const StyledProgressValue = Styled(ProgressBarValue)<{
  appearance?: GenericProgram['category'];
}>`
  ${({ appearance, theme }) =>
    appearance === ProgramCategory['Challenges']
      ? `background-color: ${theme.colors.primary.green};`
      : ''}
`;

type Props = {
  title: string;
  description: string;
  instructors: ProgramInstructorsFragment[];
  currentVideo: CurrentElement;
  progress: number;
  programsAccess: ProgramAccess | CourseAccess;
  assetURL: string;
  themes: GenericProgram['themes'];
  duration: GenericProgram['duration'];
  programCategory?: GenericProgram['category'];
  enableGiftPurchase: boolean;
  canExtendAccess: boolean;
};

const AccessProgramHeaderSection = (props: Props) => {
  const {
    title,
    instructors = [],
    currentVideo,
    progress,
    programsAccess,
    programCategory,
    description = null,
    themes = null,
    duration = null,
    assetURL,
    enableGiftPurchase,
    canExtendAccess,
  } = props;

  return (
    <Container
      flexDirection={['column', 'column', 'row']}
      flexWrap="wrap"
      alignItems={['initial', 'initial', 'center']}
      justifyContent="space-between"
      data-testid="AccessProgramHeaderSection"
      px={20}
      py={20}
    >
      <ProgramDetails>
        <Flex
          alignItems="center"
          data-testid="AccessProgramHeaderSection--Progress"
        >
          <StyledProgressBar data-testid="AccessProgramHeaderSection--ProgressBar">
            <StyledProgressValue
              progressValue={progress}
              appearance={programCategory}
            />
          </StyledProgressBar>
          <ProgressText data-testid="AccessProgramHeaderSection--ProgressValue">
            <Trans
              id="metadata.progressPercentage"
              values={{ percentage: Math.trunc(progress) }}
            />
          </ProgressText>
        </Flex>
        <ProgramName>{title}</ProgramName>
        {description ? <p>{description}</p> : null}
        <Flex alignItems="start" mt={25}>
          {assetURL ? (
            <DownloadButton
              href={assetURL}
              target="_blank"
              data-testid="download-pdf--btn"
              appearance={programCategory}
            >
              {programCategory === ProgramCategory['Courses'] ? (
                <Trans id="course.downloadPDF" />
              ) : (
                <Trans id="program.downloadPDF" />
              )}
            </DownloadButton>
          ) : null}
          <ProgramExpandableInstructors
            programTitle={title}
            instructors={instructors}
            programCategory={programCategory}
          />
        </Flex>
      </ProgramDetails>
      <ContinueProgramCard
        currentVideo={currentVideo}
        programAccess={programsAccess}
        programCategory={programCategory}
        themes={themes}
        duration={duration}
        enableGiftPurchase={enableGiftPurchase}
        canExtendAccess={canExtendAccess}
      />
    </Container>
  );
};

export default AccessProgramHeaderSection;
